import { z } from 'zod'

import { calendarDateFromStorage, isCalendarDate } from '../utils/calendar-date'
import { CustomFieldValueSchema } from './custom-field.types'
import { inventoryAllocationFields } from './inventory-allocation'
import { decimalSchema } from './prisma-zod'

// ==========================================
// Enums
// ==========================================

export enum PurchaseOrderStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  CONFIRMED = 'CONFIRMED',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED',
  CLOSED = 'CLOSED',
}

// ==========================================
// Schemas
// ==========================================

export const SupplierSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(255),

  // Contact Information
  email: z.string().email().optional().nullable(),
  phone: z.string().max(50).optional().nullable(),
  website: z.string().url().optional().nullable(),
  contactPerson: z.string().max(255).optional().nullable(),

  // Address Information
  address: z.record(z.string(), z.any()).default({}),

  // Business Information
  taxId: z.string().max(100).optional().nullable(),
  // Denormalized net days for the selected payment term (0 = due on receipt,
  // null = non-day terms like Annual/COD). Synced server-side from
  // SupplierPaymentTerm.netDays when paymentTermCode is set.
  paymentTerms: z.number().int().min(0).optional().nullable(),
  // SupplierPaymentTerm.code for this org — source of truth for the term.
  paymentTermCode: z.string().max(50).optional().nullable(),
  currency: z.string().length(3).default('USD'),

  // Status and Settings
  isActive: z.boolean().default(true),
  isPreferred: z.boolean().default(false),
  // Vendor lifecycle stage (CrmStage.stageId for moduleType=SUPPLIER_LIFECYCLE)
  lifecycleStage: z.string().max(30).default('active'),

  // Performance Metrics
  rating: z.number().min(1).max(5).optional().nullable(),
  onTimeDelivery: z.number().min(0).max(100).optional().nullable(),
  qualityScore: z.number().min(1).max(5).optional().nullable(),

  // Financial Information
  totalPurchases: decimalSchema.default('0'),
  totalOrders: z.number().int().min(0).default(0),
  avgOrderValue: decimalSchema.default('0'),

  // Organization Reference
  organizationId: z.string().uuid(),

  // Metadata and Audit
  notes: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional().nullable(),
})

export const SupplierContactSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  title: z.string().max(255).optional().nullable(),
  email: z.string().email().optional().nullable(),
  phone: z.string().max(50).optional().nullable(),
  isPrimary: z.boolean().default(false),

  supplierId: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const SupplierProductSchema = z.object({
  id: z.string().uuid(),
  supplierSku: z.string().max(100),
  supplierName: z.string().max(255),
  unitCost: decimalSchema,
  minimumOrderQty: z.number().int().positive().default(1),
  leadTimeDays: z.number().int().positive().default(1),
  isActive: z.boolean().default(true),

  // References
  inventoryItemId: z.string().uuid(),
  supplierId: z.string().uuid(),

  // Metadata and Audit
  notes: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const CreateSupplierProductSchema = SupplierProductSchema.omit({
  id: true,
  supplierId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  supplierSku: z.string().max(100).optional().default(''),
  supplierName: z.string().max(255).optional().default(''),
  unitCost: z.number().finite().nonnegative(),
  minimumOrderQty: z.number().int().positive().default(1),
  leadTimeDays: z.number().int().positive().default(1),
  isActive: z.boolean().default(true),
  notes: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.any()).default({}),
})

export const PurchaseOrderSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string().max(100),
  status: z.nativeEnum(PurchaseOrderStatus).default(PurchaseOrderStatus.DRAFT),

  // Supplier Information
  supplierId: z.string().uuid(),

  // Order Details
  orderDate: z.date(),
  expectedDate: z.date().optional().nullable(),
  deliveredDate: z.date().optional().nullable(),

  // Financial Information
  subtotal: decimalSchema.default('0'),
  taxAmount: decimalSchema.default('0'),
  shippingCost: decimalSchema.default('0'),
  discountAmount: decimalSchema.default('0'),
  totalAmount: decimalSchema.default('0'),

  // Organization Reference
  organizationId: z.string().uuid(),

  // Metadata and Audit
  notes: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional().nullable(),
})

export const PurchaseOrderItemSchema = z.object({
  id: z.string().uuid(),
  quantity: z.number().int().positive(),
  unitCost: decimalSchema,
  totalCost: decimalSchema,
  receivedQty: z.number().int().min(0).default(0),

  purchaseOrderId: z.string().uuid(),
  inventoryItemId: z.string().uuid(),

  // Metadata and Audit
  notes: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
})

// ==========================================
// Extended Schemas with Relations
// ==========================================

export const SupplierWithRelationsSchema = SupplierSchema.extend({
  contacts: z.array(SupplierContactSchema).optional(),
  products: z.array(SupplierProductSchema).optional(),
  purchaseOrders: z.array(PurchaseOrderSchema).optional(),
  _count: z
    .object({
      contacts: z.number().optional(),
      products: z.number().optional(),
      purchaseOrders: z.number().optional(),
      inventoryItems: z.number().optional(),
      documents: z.number().optional(),
      notes: z.number().optional(),
    })
    .optional(),
})

export const PurchaseOrderWithRelationsSchema = PurchaseOrderSchema.extend({
  supplier: SupplierSchema.pick({
    id: true,
    name: true,
    code: true,
    currency: true,
  }).optional(),
  items: z
    .array(
      PurchaseOrderItemSchema.extend({
        inventoryItem: z
          .object({
            id: z.string().uuid(),
            name: z.string(),
            sku: z.string(),
            isSerialized: z.boolean(),
            hasBatches: z.boolean(),
            defaultLocationId: z.string().nullable(),
          })
          .optional(),
      }),
    )
    .optional(),
  _count: z
    .object({
      items: z.number().optional(),
    })
    .optional(),
})

export const SupplierProductWithRelationsSchema = SupplierProductSchema.extend({
  supplier: SupplierSchema.optional(),
  inventoryItem: z.any().optional(), // InventoryItemSchema
})

// ==========================================
// Form Schemas
// ==========================================

export const CreateSupplierSchema = SupplierSchema.omit({
  id: true,
  totalPurchases: true,
  totalOrders: true,
  avgOrderValue: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).extend({
  contacts: z
    .array(
      SupplierContactSchema.omit({
        id: true,
        supplierId: true,
        createdAt: true,
        updatedAt: true,
      }),
    )
    .optional(),
})

export const UpdateSupplierSchema = CreateSupplierSchema.partial().extend({
  id: z.string().uuid(),
})

// Write commands intentionally exclude storage identity, received quantities,
// calculated totals and arbitrary metadata. Calendar fields retain their day.
const purchaseDay = z.preprocess(
  value => (value instanceof Date ? calendarDateFromStorage(value) : value),
  z.string().refine(isCalendarDate),
)
const purchaseMoney = z.union([
  z
    .number()
    .finite()
    .nonnegative()
    .refine(
      value => /^\d+(?:\.\d{1,2})?$/.test(String(value)),
      'Enter at most two decimal places',
    ),
  z.string().regex(/^\d+(?:\.\d{1,2})?$/),
])
const purchaseNotes = z.string().max(10000).nullable().optional()
const purchaseOrderFields = z
  .object({
    supplierId: z.string().uuid(),
    orderDate: purchaseDay.optional(),
    expectedDate: purchaseDay.nullable().optional(),
    items: z
      .array(
        z
          .object({
            inventoryItemId: z.string().uuid(),
            quantity: z.number().int().positive().max(2147483647),
            unitCost: purchaseMoney,
            notes: purchaseNotes,
          })
          .strict(),
      )
      .min(1)
      .max(100),
    taxAmount: purchaseMoney.optional(),
    shippingCost: purchaseMoney.optional(),
    discountAmount: purchaseMoney.optional(),
    notes: purchaseNotes,
    termsAndConditions: purchaseNotes,
  })
  .strict()
export const CreatePurchaseOrderSchema = purchaseOrderFields.extend({
  operationId: z.string().uuid(),
  status: z.literal(PurchaseOrderStatus.DRAFT).optional(),
})
export const UpdatePurchaseOrderSchema = purchaseOrderFields.partial().strict()
export const ReceivePurchaseOrderSchema = z
  .object({
    operationId: z.string().uuid(),
    receivedDate: purchaseDay.optional(),
    confirmedCurrency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    deliveryNotes: purchaseNotes,
    receivedBy: z.string().max(255).optional(),
    inspectionNotes: purchaseNotes,
    items: z
      .array(
        z
          .object({
            purchaseOrderItemId: z.string().uuid().optional(),
            inventoryItemId: z.string().uuid().optional(),
            receivedQty: z.number().int().positive().max(2147483647),
            locationId: z.string().uuid().optional(),
            condition: z.literal('good').optional(),
            notes: purchaseNotes,
            ...inventoryAllocationFields,
          })
          .strict()
          .refine(row => !!row.purchaseOrderItemId || !!row.inventoryItemId),
      )
      .min(1)
      .max(100),
  })
  .strict()
export type ReceivePurchaseOrder = z.infer<typeof ReceivePurchaseOrderSchema>
export const purchaseOrderCreatePayloadSchema = CreatePurchaseOrderSchema.omit({
  operationId: true,
})
export const purchaseOrderReceiptPayloadSchema =
  ReceivePurchaseOrderSchema.omit({ operationId: true }).extend({
    purchaseOrderId: z.string().uuid(),
  })

// ==========================================
// API Response Schemas
// ==========================================

export const SupplierPerformanceSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  totalOrders: z.number().int(),
  completedOrders: z.number().int(),
  onTimeDeliveryRate: z.number().nullable(),
  averageLeadTime: z.number().nullable(),
  qualityScore: z.number().nullable(),
  deliverySampleSize: z.number().int(),
})

// ==========================================
// Type Exports
// ==========================================

export type Supplier = z.infer<typeof SupplierSchema>
export type SupplierContact = z.infer<typeof SupplierContactSchema>
export type SupplierProduct = z.infer<typeof SupplierProductSchema>
export type CreateSupplierProduct = z.infer<typeof CreateSupplierProductSchema>
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>
export type PurchaseOrderItem = z.infer<typeof PurchaseOrderItemSchema>

export type SupplierWithRelations = z.infer<typeof SupplierWithRelationsSchema>
export type PurchaseOrderWithRelations = z.infer<
  typeof PurchaseOrderWithRelationsSchema
>
export type SupplierProductWithRelations = z.infer<
  typeof SupplierProductWithRelationsSchema
>

export type CreateSupplier = z.infer<typeof CreateSupplierSchema>
export type UpdateSupplier = z.infer<typeof UpdateSupplierSchema>
export type CreatePurchaseOrder = z.infer<typeof CreatePurchaseOrderSchema>
export type UpdatePurchaseOrder = z.infer<typeof UpdatePurchaseOrderSchema>

export type SupplierPerformance = z.infer<typeof SupplierPerformanceSchema>

// ==========================================
// Includes for Prisma queries
// ==========================================

export const supplierInclude = {
  supplierContacts: true,
  _count: {
    select: {
      supplierContacts: true,
      supplierProducts: true,
    },
  },
}

export const purchaseOrderInclude = {
  supplier: true,
  items: {
    include: {
      inventoryItem: true,
    },
  },
  _count: {
    select: {
      items: true,
    },
  },
}

export const supplierProductInclude = {
  supplier: true,
  inventoryItem: true,
}

/** A supplier's current default cannot establish the currency of an old order. */
export function purchaseOrderCurrency(
  order: { metadata?: unknown } | null | undefined,
): string | null {
  const metadata = order?.metadata
  const currency =
    metadata && typeof metadata === 'object' && 'currency' in metadata
      ? metadata.currency
      : null
  return typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)
    ? currency
    : null
}

/** Product quotes use a captured currency; the supplier's current default is not historical proof. */
export function supplierProductCurrency(
  product: { metadata?: unknown } | null | undefined,
): string | null {
  return purchaseOrderCurrency(product)
}
